import { GameState, PieceType, State } from '../../types';

export const ASTRA_VERSION = 'astra-1.0.0';
export const MATE = 1000000;
export const PIECE_VALUES: Record<PieceType, number> = {
  Bear:130, Crane:105, Frog:105, Fungi:115, Goat:110, Monkey:115,
  Octopus:120, Penguin:110, Rhino:125, Snake:115, Tree:100, Wolf:110, Virus:120,
};
export interface EvaluationTerms {
  material: number; race: number; activity: number; coordination: number; abilities: number; restrictions: number; total: number;
}

/** Light-positive score. A normal piece is approximately 100; these are hypotheses, not learned values. */
export function evaluate(state: GameState): EvaluationTerms {
  const terms: EvaluationTerms = {material:0,race:0,activity:0,coordination:0,abilities:0,restrictions:0,total:0};
  const units = state.board.flatMap((row,r)=>row.flatMap((s,c)=>s.Pieces.filter(p=>!p.isShadow).map(p=>({p,r,c}))));
  for (let row=0;row<8;row++) for (let col=0;col<5;col++) {
    const pieces = state.board[row][col].Pieces;
    for (const p of pieces) {
      if (p.isShadow) continue;
      const sign = p.isLight ? 1 : -1;
      const advance = p.isLight ? row : 7-row;
      const enemies=units.filter(u=>u.p.isLight!==p.isLight);
      let value = PIECE_VALUES[p.type];
      if(p.type===PieceType.Penguin && !p.isKing) value=70;
      if(p.type===PieceType.Crane) value+=enemies.filter(u=>[PieceType.Fungi,PieceType.Monkey,PieceType.Goat].includes(u.p.type)).length*5-
        enemies.filter(u=>[PieceType.Bear,PieceType.Wolf,PieceType.Rhino].includes(u.p.type)).length*5;
      if(p.type===PieceType.Octopus) value+=enemies.filter(u=>[PieceType.Bear,PieceType.Wolf,PieceType.Snake,PieceType.Frog,PieceType.Crane].includes(u.p.type)).length*4;
      terms.material += sign * (value + (p.isKing ? 10000 : 0));
      // King progress accelerates near the finish. Search must prove the route safe.
      if (p.isKing) terms.race += sign * [0,4,10,22,40,70,120,200][advance];
      else if (p.type !== PieceType.Virus) terms.activity += sign * (Math.min(advance,4)*3 + (2-Math.abs(col-2))*2);
      if (p.type === PieceType.Frog && !p.hasLilyJumped) terms.abilities += sign*45;
      if (p.type === PieceType.Snake && !p.hasShed) terms.abilities += sign*(p.isKing ? 65 : 28);
      if (p.type === PieceType.Rhino && !p.hasCharged) terms.abilities += sign*10;
      if (p.type === PieceType.Monkey && pieces.some(x => x.type === PieceType.Tree)) terms.abilities += sign*12;
      // Modest long-term formation terms: never worth throwing away a piece.
      const friends=units.filter(u=>u.p.isLight===p.isLight && u.p.id!==p.id);
      const near=friends.filter(u=>Math.max(Math.abs(u.r-row),Math.abs(u.c-col))<=2);
      if(p.type===PieceType.Snake && friends.some(u=>u.p.type===PieceType.Frog && !u.p.hasLilyJumped && u.c===col &&
        (p.isLight?row-u.r:u.r-row)>=1 && (p.isLight?row-u.r:u.r-row)<=3)) terms.coordination+=sign*26;
      if(p.type===PieceType.Rhino && !p.hasCharged && near.some(u=>[PieceType.Frog,PieceType.Penguin].includes(u.p.type))) terms.coordination+=sign*16;
      if(p.type===PieceType.Virus) {
        const reachable=(a:{r:number;c:number},b:{r:number;c:number})=>{
          const dr=Math.abs(a.r-b.r),dc=Math.abs(a.c-b.c);
          return (dr===2 && (dc===0||dc===2)) || (dc===2 && dr===0);
        };
        let staging=0;
        for(const host of friends.filter(u=>!u.p.isKing && u.p.type!==PieceType.Tree && reachable({r:row,c:col},u))) {
          const first=enemies.filter(u=>reachable(host,u));
          const followups=enemies.filter(u=>first.some(v=>u.p.id!==v.p.id && reachable(v,u)));
          if(first.length && followups.length) staging=Math.max(staging,24+Math.min(3,followups.length)*8);
        }
        terms.coordination+=sign*staging;
        if(!staging && !enemies.some(u=>reachable({r:row,c:col},u))) terms.coordination-=sign*18;
      }
      if (p.isKing) {
        const escorts=near.filter(u=>!u.p.isKing && (p.isLight?u.r>=row:u.r<=row));
        terms.coordination += sign*Math.min(2,escorts.length)*10;
        if(advance>=3 && escorts.length===0 && units.length>5) terms.coordination-=sign*25;
        if(p.type===PieceType.Penguin) terms.coordination+=sign*Math.max(0,10-units.length)*7;
        if(p.type===PieceType.Goat) terms.coordination+=sign*Math.min(10,units.length)*3;
      } else {
        // Develop a connected front instead of one isolated attacker.
        if(advance>=2 && advance<=5) terms.coordination+=sign*(near.length ? 8 : -12);
        // A nearby Tree gives Monkey repeatable mobility beyond immediate captures.
        if(p.type===PieceType.Monkey && near.some(u=>u.p.type===PieceType.Tree)) terms.coordination+=sign*24;
        // Octopus benefits from supporting the front, not sitting on its home rank.
        if(p.type===PieceType.Octopus && advance>=2 && advance<=4) terms.coordination+=sign*12;
        // Preserve a defensive screen on the enemy king's approach.
        const enemyKing=units.find(u=>u.p.isKing && u.p.isLight!==p.isLight);
        if(enemyKing && Math.abs(enemyKing.c-col)<=1 && (p.isLight?row<enemyKing.r:row>enemyKing.r) &&
          Math.abs(enemyKing.r-row)<=3) terms.coordination+=sign*12;
      }
      if (p.isEntangled || p.isMeadowed || p.isDormant) terms.restrictions -= sign*(p.isKing ? 90 : value*0.35);
      if (p.poisonedStatus.isPoisoned) terms.restrictions -= sign*(p.isKing ? 60 : 25);
      if (p.zombifiedStatus.isZombified) terms.restrictions -= sign*value*0.6;
      if (p.type !== PieceType.Virus && pieces.some(x => x.type === PieceType.Virus)) terms.restrictions -= sign*value*0.8;
    }
  }
  terms.total = terms.material+terms.race+terms.activity+terms.coordination+terms.abilities+terms.restrictions;
  return terms;
}

export function terminalScore(state: GameState, rootLight: boolean, ply: number): number | undefined {
  if (state.state === State.Draw || state.state === State.Terminated || (state as any).isDraw) return 0;
  if (state.state === State.PostGame) {
    if (state.lightWon === undefined) return 0;
    return state.lightWon === rootLight ? MATE-ply : -MATE+ply;
  }
  return undefined;
}
